/**
 * Sound and Haptics Service
 *
 * Handles sound effects and haptic feedback throughout the app
 * - Respects user preferences (stored in localStorage)
 * - Supports volume control
 * - Provides haptic feedback on mobile devices
 * - Preloads sounds for better performance
 */

import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';
import { logger } from '../utils/logger';

type SoundName =
  // Dice Match
  | 'dice-place'
  | 'dice-merge'
  | 'dice-sparkle'
  | 'dice-gameover'
  // Nerdle
  | 'letter-correct'
  | 'letter-wrong'
  | 'word-submit'
  | 'game-win'
  // Pip's Guild Quest
  | 'turn-start'
  | 'combat-hit'
  | 'combat-miss'
  | 'level-up'
  // Sanctuary
  | 'creature-catch'
  | 'habitat-place'
  | 'unlock'
  | 'sanctuary-unlock'
  // Dungeon Escape
  | 'footstep'
  | 'door-open'
  | 'item-pickup'
  | 'enemy-encounter'
  // UI
  | 'button-click'
  | 'success'
  | 'error'
  | 'notification';

type HapticType = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error';

interface SoundOptions {
  volume?: number; // 0.0 to 1.0
  loop?: boolean;
  haptic?: HapticType;
}

const STORAGE_KEY_SOUND_ENABLED = 'app_sound_enabled';
const STORAGE_KEY_HAPTICS_ENABLED = 'app_haptics_enabled';
const STORAGE_KEY_VOLUME = 'app_sound_volume';

class SoundService {
  private sounds: Map<SoundName, HTMLAudioElement> = new Map();
  private soundEnabled: boolean = true;
  private hapticsEnabled: boolean = true;
  private volume: number = 0.7;
  private isNativePlatform: boolean = false;
  private preferencesLoaded: boolean = false;

  constructor() {
    this.isNativePlatform = Capacitor.isNativePlatform();
    // Don't call loadPreferences here to avoid TDZ issues
    // It will be called lazily on first use
  }

  /**
   * Load user preferences from localStorage
   */
  private loadPreferences(): void {
    if (this.preferencesLoaded) {
      return; // Already loaded
    }

    try {
      const soundPref = localStorage.getItem(STORAGE_KEY_SOUND_ENABLED);
      const hapticsPref = localStorage.getItem(STORAGE_KEY_HAPTICS_ENABLED);
      const volumePref = localStorage.getItem(STORAGE_KEY_VOLUME);

      if (soundPref !== null) {
        this.soundEnabled = soundPref === 'true';
      }

      if (hapticsPref !== null) {
        this.hapticsEnabled = hapticsPref === 'true';
      }

      if (volumePref !== null) {
        this.volume = parseFloat(volumePref);
      }

      logger.log('[Sound] Preferences loaded:', {
        sound: this.soundEnabled,
        haptics: this.hapticsEnabled,
        volume: this.volume,
      });
      this.preferencesLoaded = true;
    } catch (error) {
      logger.error('Failed to load sound preferences:', error);
      this.preferencesLoaded = true; // Mark as loaded even if failed
    }
  }

  /**
   * Save preferences to localStorage
   */
  private savePreferences(): void {
    try {
      localStorage.setItem(STORAGE_KEY_SOUND_ENABLED, String(this.soundEnabled));
      localStorage.setItem(STORAGE_KEY_HAPTICS_ENABLED, String(this.hapticsEnabled));
      localStorage.setItem(STORAGE_KEY_VOLUME, String(this.volume));
    } catch (error) {
      logger.error('Failed to save sound preferences:', error);
    }
  }

  /**
   * Load a sound file and cache it
   */
  loadSound(name: SoundName, path: string): void {
    try {
      const audio = new Audio(path);
      audio.preload = 'auto';

      // Handle load errors gracefully
      audio.addEventListener('error', (e) => {
        logger.warn(`[Sound] Failed to load ${name} from ${path}`, e);
        // Remove from cache if it fails to load
        this.sounds.delete(name);
      });

      this.sounds.set(name, audio);
      logger.log(`[Sound] Loaded sound: ${name}`);
    } catch (error) {
      logger.error(`Failed to load sound ${name}:`, error);
    }
  }

  /**
   * Load multiple sounds at once
   */
  loadSounds(soundMap: Partial<Record<SoundName, string>>): void {
    Object.entries(soundMap).forEach(([name, path]) => {
      this.loadSound(name as SoundName, path as string);
    });
  }

  /**
   * Play a sound effect
   */
  play(name: SoundName, options: SoundOptions = {}): void {
    this.loadPreferences(); // Lazy load preferences
    const { volume = this.volume, loop = false, haptic } = options;

    // Play sound
    if (this.soundEnabled) {
      const sound = this.sounds.get(name);
      if (sound) {
        try {
          sound.currentTime = 0;
          sound.volume = Math.max(0, Math.min(1, volume));
          sound.loop = loop;
          sound.play().catch((error) => {
            // Ignore autoplay errors (common on web)
            logger.log(`Sound autoplay prevented for ${name}:`, error.message);
          });
        } catch (error) {
          logger.error(`Failed to play sound ${name}:`, error);
        }
      } else {
        logger.warn(`Sound not loaded: ${name}`);
      }
    }

    // Trigger haptic feedback if specified
    if (haptic) {
      this.haptic(haptic);
    }
  }

  /**
   * Stop a sound
   */
  stop(name: SoundName): void {
    const sound = this.sounds.get(name);
    if (sound) {
      sound.pause();
      sound.currentTime = 0;
    }
  }

  /**
   * Stop all sounds
   */
  stopAll(): void {
    this.sounds.forEach((sound) => {
      sound.pause();
      sound.currentTime = 0;
    });
  }

  /**
   * Trigger haptic feedback
   */
  async haptic(type: HapticType = 'light'): Promise<void> {
    this.loadPreferences(); // Lazy load preferences
    if (!this.hapticsEnabled || !this.isNativePlatform) {
      return;
    }

    try {
      switch (type) {
        case 'light':
          await Haptics.impact({ style: ImpactStyle.Light });
          break;
        case 'medium':
          await Haptics.impact({ style: ImpactStyle.Medium });
          break;
        case 'heavy':
          await Haptics.impact({ style: ImpactStyle.Heavy });
          break;
        case 'success':
          await Haptics.notification({ type: NotificationType.Success });
          break;
        case 'warning':
          await Haptics.notification({ type: NotificationType.Warning });
          break;
        case 'error':
          await Haptics.notification({ type: NotificationType.Error });
          break;
      }
    } catch (error) {
      // Haptics may not be available on all devices
      logger.log('Haptics not available:', error);
    }
  }

  /**
   * Enable/disable sound
   */
  setSoundEnabled(enabled: boolean): void {
    this.soundEnabled = enabled;
    this.savePreferences();
    logger.log(`[Sound] Sound ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Enable/disable haptics
   */
  setHapticsEnabled(enabled: boolean): void {
    this.hapticsEnabled = enabled;
    this.savePreferences();
    logger.log(`[Haptics] Haptics ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Set volume (0.0 to 1.0)
   */
  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    this.savePreferences();
    logger.log(`[Sound] Volume set to ${Math.round(this.volume * 100)}%`);
  }

  /**
   * Get current settings
   */
  getSettings(): {
    soundEnabled: boolean;
    hapticsEnabled: boolean;
    volume: number;
  } {
    this.loadPreferences(); // Lazy load preferences
    return {
      soundEnabled: this.soundEnabled,
      hapticsEnabled: this.hapticsEnabled,
      volume: this.volume,
    };
  }

  /**
   * Check if sound is loaded
   */
  isSoundLoaded(name: SoundName): boolean {
    return this.sounds.has(name);
  }

  /**
   * Get all loaded sounds
   */
  getLoadedSounds(): SoundName[] {
    return Array.from(this.sounds.keys());
  }
}

// Export singleton instance
export const soundService = new SoundService();

// Convenience functions for common interactions
export const playButtonClick = () => soundService.play('button-click', { haptic: 'light' });
export const playSuccess = () => soundService.play('success', { haptic: 'success' });
export const playError = () => soundService.play('error', { haptic: 'error' });
export const playNotification = () => soundService.play('notification', { haptic: 'medium' });

// Haptic-only helpers (for interactions without sound)
export const hapticLight = () => soundService.haptic('light');
export const hapticMedium = () => soundService.haptic('medium');
export const hapticHeavy = () => soundService.haptic('heavy');
export const hapticSuccess = () => soundService.haptic('success');
export const hapticWarning = () => soundService.haptic('warning');
export const hapticError = () => soundService.haptic('error');
